import { effect, inject, Injectable } from '@angular/core';
import { ChatMessage, ChatThread } from '@models';
import { ChatService, MeService, VM_DEMO_PATIENTS } from '@services';
import { BehaviorSubject, defer, map, of } from 'rxjs';

/** Registered only with the development voicemail gateway, never for normal chats. */
@Injectable()
export class VoicemailDemoChatService extends ChatService {
  private readonly me = inject(MeService);
  private readonly threads = new BehaviorSubject<ChatThread[]>([]);
  private readonly conversations = new Map<
    string,
    BehaviorSubject<ChatMessage[]>
  >();
  private providerId: string | undefined;

  constructor() {
    super();
    effect(() => this.resetForProvider(this.me.me()?.id));
  }

  threadForPatient(patientId: string): string | null {
    this.resetForProvider(this.me.userId);
    const patient = VM_DEMO_PATIENTS.find(item => item.id === patientId);
    if (!patient || !this.me.userId) return null;
    const threadId = `voicemail-sample-${patient.id}`;
    if (this.conversations.has(threadId)) return threadId;
    const [givenName, ...familyName] = patient.name.split(' ');
    const sentAt = new Date();
    const text = `You're welcome, ${givenName}. Let me know if you have a question before your next visit.`;
    this.conversations.set(
      threadId,
      new BehaviorSubject<ChatMessage[]>([
        {
          id: `${threadId}-patient`,
          senderId: patient.id,
          senderName: patient.name,
          text: 'Thank you for the information. I will see you at my next appointment.',
          sentAt: new Date(sentAt.getTime() - 60_000),
        },
        {
          id: `${threadId}-welcome`,
          senderId: this.me.userId,
          senderName:
            `${this.me.me()?.givenName ?? ''} ${this.me.me()?.familyName ?? ''}`.trim(),
          text,
          sentAt,
        },
      ])
    );
    this.threads.next([
      ...this.threads.value,
      {
        threadId,
        counterparty: {
          userId: patient.id,
          role: 'Patient',
          givenName,
          familyName: familyName.join(' '),
          avatarUrl: null,
          email: null,
        },
        lastMessagePreview: text,
        lastMessageAt: sentAt,
        unreadCount: 0,
      },
    ]);
    return threadId;
  }

  override getMessages(threadId: string) {
    return defer(() => of(this.conversations.get(threadId)?.value ?? []));
  }

  override watchMessages(threadId: string) {
    return defer(
      () => this.conversations.get(threadId)?.asObservable() ?? of([])
    );
  }

  override sendMessage(threadId: string, text: string) {
    return defer(() => {
      this.resetForProvider(this.me.userId);
      const conversation = this.conversations.get(threadId);
      if (!conversation || !this.me.userId)
        throw new Error('Sample conversation unavailable.');
      const sentAt = new Date();
      conversation.next([
        ...conversation.value,
        {
          id: crypto.randomUUID(),
          senderId: this.me.userId,
          senderName:
            `${this.me.me()?.givenName ?? ''} ${this.me.me()?.familyName ?? ''}`.trim(),
          text,
          sentAt,
        },
      ]);
      this.threads.next(
        this.threads.value.map(thread =>
          thread.threadId === threadId
            ? { ...thread, lastMessagePreview: text, lastMessageAt: sentAt }
            : thread
        )
      );
      return of(undefined);
    });
  }

  override markThreadAsRead(_threadId: string) {
    return of(undefined);
  }

  override getThreads() {
    return defer(() => of(this.threads.value));
  }

  override watchThreads() {
    return this.threads.pipe(map(threads => [...threads]));
  }

  private resetForProvider(providerId: string | undefined): void {
    if (providerId === this.providerId) return;
    this.providerId = providerId;
    for (const conversation of this.conversations.values())
      conversation.complete();
    this.conversations.clear();
    this.threads.next([]);
  }
}
